import React, { useEffect, useState, useMemo } from 'react';
import useSWR from 'swr';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import { useAxios } from 'use-axios-client';

// ---------------------------------------------------------
import classNames from 'classnames';
import Listing from '@lib/models/listing';
import {
  formatMoney,
  formatNumber,
  getSlug,
  fetcher,
  truncateListingName,
  parseDateWithFormatMMMDDAndTime
} from '@lib/utils';

import {
  fetchAssociatedAgent,
  execAssociatedAgentQuery,
  getAssociatedAgentItem
} from '@lib/associatedAgent';

import { updateIsSibcyListingFilter } from '../../store/slices/miscSlice';
import communityLookup from '@src/data/community-info.json';
import builderDevelopments from '@src/data/builder-development-info.json';
import { useAgentData } from '@lib/hooks';

// ---------------------------------------------------------

import Layout from '@layout/index';
import Container from '@layout/container';
import Image from '@components/image';
import Button from '@components/button';
import PostList from '@components/post-list';
import SellWithSibcy from '@components/sell-with-sibcy';
import SimilarProperties from '@components/similar-properties';
import Accordion from '@components/accordion';
import MortgageCalculator from '@components/mortgage-calculator';
import TopicCarousel from '@components/topic-carousel';
import Link from '@components/link';

// ---------------------------------------------------------

import PropertyHeader from './components/property-header';
import PropertyGallery from './components/property-gallery';
import PropertyAlerts from './components/property-alerts';
import PropertySummary from './components/property-summary';
import PropertyDetails from './components/property-details';
import PropertySidebar from './components/property-sidebar';
import PropertyModals from './components/property-modals';

// ---------------------------------------------------------

import {
  page,
  page_container,
  top_content_container,
  left,
  right,
  right_margin_top_600,
  bottom,
  grid_container,
  copyright_section,
  sticky_footer,
  section_header,
  calculator,
  breadcrumb_container,
  breadcrumbs,
  listing_courtesy_of
} from './styles.module.scss';

// ---------------------------------------------------------

import { ListingProvider } from './context';

// ---------------------------------------------------------

const ListingDetailPageTemplate = (props) => {
  const {
    alerts,
    interestRates,
    listingId: id,
    videoUrl,
    alertPills,
    virtualTourUrl,
    primaryDisplayAgent,
    secondaryDisplayAgent,
    isIdxListing,
    address,
    city,
    state,
    statusId,
    zip,
    bedroomCount,
    bathroomCount,
    builder,
    development,
    squareFootage,
    acres,
    price,
    priceFormatted,
    mlsNumber,
    mainPhoto,
    fullBathroomCount,
    partBathroomCount,
    photos,
    informationRequestDefaultComments,
    marketingRemarks,
    dataRefreshed,
    rooms,
    features,
    similarProperties,
    updateNoteListings,
    canonicalUrl,
    openHouses,
    listingCourtesyOf,
    mlsCopyright,
    listingUrl,
    longitude,
    latitude,
    propertyTypeDisplay,
    propertyTypeId,
    sharingServicesCollection,
    mapLocationExternalLink,
    directions,
    listingCommunity,
    nearbyCommunities,
    sellerConcessions,
    associatedAgent: associatedAgentProp,
    county,
    shouldShowSibcyAgent
  } = props;

  const truncatedListingName = truncateListingName(address, city, state, zip);

  // ---------------------------------------------------------

  const { saved, authenticated: isLoggedIn, userInfo } = useSelector((state) => state.user);
  const router = useRouter();
  const listing = new Listing(id);
  const dispatch = useDispatch();
  const {
    associatedAgent,
    setAssociatedAgent,
    setAssociatedAgentToSessionStorage,
    getAssociatedAgentFromSessionStorage
  } = useAgentData(associatedAgentProp);
  const [views, updateViews] = useState(0);
  const [selectedArea, setSelectedArea] = useState('Photos');
  const [isMedium, setIsMedium] = useState(null);
  const [notificationModalIsOpen, setNotificationModalIsOpen] = useState(false);
  const [requestATourModalIsOpen, setRequestATourModalIsOpen] = useState(router.query.tourModal);
  const [preApprovalModalIsOpen, setPreApprovalModalIsOpen] = useState(false);
  const [notesModalIsOpen, setNotesModalIsOpen] = useState(false);
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);
  const [openHouseModalIsOpen, setOpenHouseModalIsOpen] = useState(false);
  const { data, refetch } = useAxios(`/api/user/account`);
  const savedListing = saved.find(({ listingId }) => props.listingId === listingId);

  const { data: userListingsData } = useSWR(
    isLoggedIn ? `/api/user/listings/${id}` : null,
    fetcher
  );
  const currentListing =
    userListingsData?.listingsByIds?.find((item) => item.listingId === id) || savedListing;

  const communities_slides = useMemo(() => {
    return [listingCommunity].concat(nearbyCommunities || []).map((item) => ({
      excerpt: item?.excerpt,
      featuredImage: item?.featuredImage || {
        height: 960,
        url: 'https://images.ctfassets.net/wzzep3ntdgx4/4V3cWpoV2LctBsJYc6SnSf/4ef45ecd14a78222b508078d9fe9d345/AdobeStock_376819599.jpeg',
        width: 1440
      },
      linkLabel: item?.linkLabel || 'View Community',
      linkUrl: `/community/${item?.slug}`,
      title: item?.title
    }));
  }, [listingCommunity, nearbyCommunities]);

  const hasSecondaryAgent = !!secondaryDisplayAgent && !!primaryDisplayAgent;
  const hasTour = statusId !== 6 && statusId !== 14;
  const hasOpenHouse = openHouses[0];
  const showMap = !!longitude && !!latitude && latitude !== 0 && latitude !== 0;
  const hasMapLocationExternalLink = mapLocationExternalLink && latitude !== 0 && longitude !== 0;
  const uniqVirtualTourUrl = virtualTourUrl !== videoUrl ? virtualTourUrl : null;

  const dates = openHouses.map((openHouse) => {
    return parseDateWithFormatMMMDDAndTime(openHouse.formattedDateTimeDisplay);
  });

  useEffect(() => {
    // Update Sibcy listing filter
    dispatch(
      updateIsSibcyListingFilter({
        group: 'isSibcyListing',
        value: !isIdxListing
      })
    );

    // Handle agent data from session storage on mount
    if (associatedAgentProp) {
      setAssociatedAgentToSessionStorage(associatedAgentProp);
    } else {
      const agentData = getAssociatedAgentFromSessionStorage();
      if (agentData) {
        setAssociatedAgent(agentData);
      }
    }
  }, [
    isIdxListing,
    associatedAgentProp,
    dispatch,
    setAssociatedAgentToSessionStorage,
    getAssociatedAgentFromSessionStorage,
    setAssociatedAgent
  ]);

  useEffect(() => {
    if (!data?.associatedAgent) return;

    const fetchAssociatedAgentData = async (variables) => {
      const data = await execAssociatedAgentQuery(variables);
      setAssociatedAgent(data?.agentSearch?.items[0] ?? []);
    };

    const variables = { agentId: data.associatedAgent };
    fetchAssociatedAgentData(variables);
  }, [data?.associatedAgent, setAssociatedAgent]);

  useEffect(() => {
    if (router.query.openhouse && hasOpenHouse) {
      setOpenHouseModalIsOpen(true);
    }
  }, [router.query.openhouse, hasOpenHouse]);

  useEffect(() => {
    const updateViewCount = async () => {
      updateViews(await listing.stats());
    };
    updateViewCount();

    const updateMedia = () => {
      setIsMedium(window.innerWidth < 1080);
    };

    window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateMedia);
      return () => window.removeEventListener('resize', updateMedia);
    }
  }, [listing]);

  // ---------------------------------------------------------

  const getBreadcrumbs = () => {
    const getBreadcrumbItems = () => {
      const items = [];
      if (state)
        items.push({
          label: state,
          url: '/browse',
          query: { search_type: 'all_listings', state }
        });

      if (zip)
        items.push({
          label: zip,
          url: '/results',
          query: {
            zip: `${zip}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      if (county)
        items.push({
          label: `${county} County`,
          url: '/results',
          query: {
            counties: `${county}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      if (city)
        items.push({
          label: city,
          url: '/results',
          query: {
            places: `${city}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      items.push({ label: address, url: null });

      return items;
    };

    return (
      <div className={breadcrumbs}>
        {getBreadcrumbItems().map((item, index) => (
          <React.Fragment key={index}>
            <span>
              {item.url ? (
                <Link url={item.url} query={item.query} className={listing_courtesy_of}>
                  {item.label}
                </Link>
              ) : (
                <span className={listing_courtesy_of}>{item.label}</span>
              )}
            </span>
            {index < getBreadcrumbItems().length - 1 && (
              <span className={listing_courtesy_of}>•</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <ListingProvider value={props}>
      <Layout
        canonicalUrl={canonicalUrl}
        meta={{
          description: marketingRemarks,
          image: {
            alt: address,
            url: mainPhoto?.midSizeImageUrl
          },
          title: truncatedListingName
        }}
        listingDetails={props}
        specializedPageSchema="realEstateSchema"
      >
        <div className={page}>
          <div className={page_container}>
            <div className={grid_container}>
              <PropertyHeader />

              <PropertyGallery
                selectedArea={selectedArea}
                setSelectedArea={setSelectedArea}
                isMedium={isMedium}
              />
            </div>
            <div className={top_content_container}>
              <div className={left}>
                <PropertyAlerts selectedArea={selectedArea} setSelectedArea={setSelectedArea} />

                <PropertySummary
                  setNotificationModalIsOpen={setNotificationModalIsOpen}
                  setNotesModalIsOpen={setNotesModalIsOpen}
                  setShareModalIsOpen={setShareModalIsOpen}
                  setPreApprovalModalIsOpen={setPreApprovalModalIsOpen}
                  setRequestATourModalIsOpen={setRequestATourModalIsOpen}
                />

                <PropertyDetails />

                {communities_slides?.length > 0 && (
                  <>
                    <h2 className={section_header}>
                      {communities_slides?.length > 1
                        ? `Explore ${listingCommunity.title} & Surrounding Area`
                        : 'Lifestyle in the Surrounding Area'}
                    </h2>
                    <TopicCarousel
                      carouselId="custom-nearby-communities"
                      theme="ldp"
                      slidesCollection={{ cards: communities_slides }}
                    />
                  </>
                )}
                <Accordion
                  listItems={{
                    items: [
                      {
                        content: {
                          value: (
                            <div className={calculator}>
                              <MortgageCalculator
                                price={price}
                                currentRates={interestRates}
                                lastUpdatedOn={interestRates?.lastUpdatedOn}
                              />
                            </div>
                          )
                        },
                        id: 'monthly-cost',
                        label: 'Monthly Cost'
                      }
                    ]
                  }}
                  openAccordionId={['monthly-cost']}
                />
                <div className={breadcrumb_container}>{getBreadcrumbs()}</div>
              </div>

              <div
                className={classNames(right, {
                  [right_margin_top_600]: photos.length <= 1 && selectedArea === 'Photos'
                })}
              >
                <PropertySidebar setRequestATourModalIsOpen={setRequestATourModalIsOpen} />
              </div>
            </div>
          </div>

          <section className={bottom}>
            {similarProperties.length ? (
              <SimilarProperties properties={similarProperties} address={address} />
            ) : null}
            <Container width="full" layout="Two thirds, one third" gutter="25">
              <SellWithSibcy />
              <PostList title="Must reads" />
            </Container>
          </section>

          <Container>
            <section className={copyright_section}>
              <div>
                <Image
                  layout="fill"
                  image={{
                    height: '22px',
                    src: mlsCopyright.logoUrl,
                    width: '96px'
                  }}
                />
              </div>

              <div>{mlsCopyright.description}</div>
            </section>
          </Container>
        </div>

        <PropertyModals
          shareModalIsOpen={shareModalIsOpen}
          setShareModalIsOpen={setShareModalIsOpen}
          preApprovalModalIsOpen={preApprovalModalIsOpen}
          setPreApprovalModalIsOpen={setPreApprovalModalIsOpen}
          notesModalIsOpen={notesModalIsOpen}
          setNotesModalIsOpen={setNotesModalIsOpen}
          notificationModalIsOpen={notificationModalIsOpen}
          setNotificationModalIsOpen={setNotificationModalIsOpen}
          requestATourModalIsOpen={requestATourModalIsOpen}
          setRequestATourModalIsOpen={setRequestATourModalIsOpen}
          openHouseModalIsOpen={openHouseModalIsOpen}
          setOpenHouseModalIsOpen={setOpenHouseModalIsOpen}
          updateNoteListings={updateNoteListings}
        />

        {hasTour && (
          <div className={sticky_footer}>
            <div>
              <Button
                theme="red"
                onClick={() => setRequestATourModalIsOpen(true)}
                label="Request Tour"
                iconType="general"
                icon="clock"
              />
            </div>
          </div>
        )}
      </Layout>
    </ListingProvider>
  );
};

export default ListingDetailPageTemplate;
